import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { darkTheme } from "@yleisradio/yds-core";
import { ExternalLink } from "@yleisradio/yds-icons-react";
import styled from "styled-components";

import { useEmbedParametersContext } from "../contexts/EmbedParametersContext";
import { UI_SIZE } from "../enums";
import { getAreenaUrl } from "../helpers/areenaLinks";
import i18n from "../service/i18n";
import { usePlayerStore } from "../store/usePlayerStore";

import { Tooltip } from "./player-ui/ControlBarButton";
import { SettingHeader } from "./settings/styles";
import { localizedLogo } from "./VideoPreviewOverlay";

interface ReturnToYleDomainProps {
  preview?: boolean;
}

const ReturnButtonWrapper = styled.a`
  display: flex;
  left: 0;
  bottom: 0;
  position: relative;
  text-decoration: none;
  color: ${darkTheme.ACTION_PRIMARY};
`;

const PreviewReturn = styled(ReturnButtonWrapper)`
  position: absolute;
  z-index: 99999;
  background-color: rgb(0 0 0 / 60%);
  border-radius: 8px;
  padding: 12px 16px;
  margin: 8px;
  span {
    display: block;
    margin: 0 10px;
  }
`;

const ReturnLinkLogo = styled.div<{
  localizedLogo: string;
}>`
  width: 107px;
  background: url(${props => `"${props.localizedLogo}"`}) center center / 100%
    no-repeat;
`;

const TopSettingsWrapper = styled.a`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-direction: row;
  flex-grow: 0;
  justify-content: space-between;
  text-decoration: none;
`;

const SettingsValue = styled.span<{ uiSize?: UI_SIZE }>`
  font-size: ${({ uiSize }) => (uiSize === UI_SIZE.LARGE ? "14px" : "12px")};
  font-weight: normal;

  svg {
    margin-left: 8px;
  }
`;

const ReturnToYleDomain: FC<PropsWithChildren<ReturnToYleDomainProps>> = ({
  preview
}) => {
  const { useStoreState } = usePlayerStore();
  const programId = useStoreState.programId();
  const isSimulcast = useStoreState.isSimulcast();
  const uiSize = useStoreState.uiSize();
  const { language } = useEmbedParametersContext();
  const device = useStoreState.device();
  const isControlsVisible = useStoreState.isControlsVisible();

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const timerRef = useRef<number>(undefined);

  const hideTooltip = useCallback(() => {
    clearTimeout(timerRef.current);
    setTooltipVisible(false);
  }, []);

  const showTooltip = useCallback(() => {
    if (!device.isMobile) {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setTooltipVisible(true);
      }, 500) as unknown as number;
    }
  }, [device]);

  useEffect(() => {
    if (!isControlsVisible) {
      hideTooltip();
    }
  }, [hideTooltip, isControlsVisible]);

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
    };
  }, []);

  const areenaLink = programId
    ? getAreenaUrl({
        isSimulcast,
        language,
        programId
      })
    : "";
  const smallUi = device.isMobile || uiSize === UI_SIZE.XSMALL;
  return (
    <>
      {preview ? (
        <PreviewReturn href={areenaLink}>
          <ReturnLinkLogo localizedLogo={localizedLogo(language)} />
          <span>{i18n("katso_yle_areenassa", { language })}</span>
          <ExternalLink />
        </PreviewReturn>
      ) : smallUi ? (
        <TopSettingsWrapper href={areenaLink}>
          <SettingHeader uiSize={uiSize}>
            {i18n("katso_yle_areenassa", { language })}
          </SettingHeader>
          <SettingsValue uiSize={uiSize}>
            <div></div>
            <ExternalLink />
          </SettingsValue>
        </TopSettingsWrapper>
      ) : (
        <ReturnButtonWrapper
          href={areenaLink}
          onBlur={hideTooltip}
          onFocus={showTooltip}
          onMouseLeave={hideTooltip}
          onMouseOver={showTooltip}>
          <ReturnLinkLogo localizedLogo={localizedLogo(language)} />
          <Tooltip visible={tooltipVisible}>
            {i18n("katso_yle_areenassa", { language })}
          </Tooltip>
        </ReturnButtonWrapper>
      )}
    </>
  );
};

export default ReturnToYleDomain;
